import { LitElement, html } from 'lit';
import { HighlineService } from '@cof/auto-refinance-node-highline';
import '@cof/fs-tooltip-wc/fs-tooltip.js';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../../shared/services/debug.service.js';
import { ApplicationService } from '../../../../services/application.service.js';
import { style } from './ssn.style.js';
import { REFI_PERSONAL_CONST } from '../../../../shared/constants/app.constants.js';
import { globalStyle } from '../../../../global-styles.style.js';
import '@cof/fs-ssn-field-wc-beta/fs-ssn-field.js';
import 'element-internals-polyfill';
import { stratum } from '../../../../../constants/stratum.js';
import { ApiService } from '../../../../core/services/api/api.service.js';

class RefiPersonalSSNComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepIdentity');
    this.values = {};
    this.coAppPrefix = '';
    this.ssnId = '';
    this.ssnMatch = false;
    this.pendingValidation = false;
    this.entered = '';
    this.validatedAgainstPrimaryapp = false;
    this.iconicV2 = ApplicationService.isIconicV2;
    this.touchedField = false;
    this.showField = false;
    this.fieldName = '';
    this.isCommunicationStepV2 = false;
    this.publicKeyPromise = null;
    this.encryptionPromise = null;
  }

  static styles = [style, globalStyle];

  static properties = {
    applicant: { type: Object, reflect: true },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    ssnMatch: { type: Boolean, attribute: 'ssn-match', reflect: true },
    pendingValidation: { type: Boolean, reflect: true },
    isCommunicationStepV2: {
      type: Boolean,
      attribute: 'is-communication-step-v2',
      reflect: true,
    },
  };

  connectedCallback() {
    super.connectedCallback();
    this.application = ApplicationService.Application;
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.ssnId = `${this.coAppPrefix}ssn`;
    this.values.ssn = this.isPrimary
      ? this.application.primaryApplicant.ssn
      : this.application.coApplicant.ssn;
    this.lastFourTaxId = this.isPrimary
      ? this.application.primaryApplicant.lastFourTaxId
      : this.application.coApplicant.lastFourTaxId;
    this.fieldName = this.isPrimary ? 'ssn' : 'co-ssn';
  }

  resetValues() {
    this.application = ApplicationService.Application;
    this.values.ssn = this.isPrimary
      ? this.application.primaryApplicant.ssn
      : this.application.coApplicant.ssn;
    this.lastFourTaxId = this.isPrimary
      ? this.application.primaryApplicant.lastFourTaxId
      : this.application.coApplicant.lastFourTaxId;

    this.ssnMatch = false;
    this.touchedField = false;
    this.showField = this.checkMaskPrefill();

    // Ensure DOM is settled before grabbing the element
    let ssnField;
    setTimeout(() => {
      ssnField = this.shadowRoot.getElementById(this.ssnId);
      ssnField.setAttribute('value', this.values.ssn);
      ssnField.checkValidity();
    }, 0);

    setTimeout(() => {
      if (this.checkMaskPrefill() && !this.checkRequired()) {
        this.dispatchEvent(
          new CustomEvent('validity', {
            detail: {
              name: 'ssn',
              isValid: true,
            },
          })
        );
      } else {
        this.dispatchEvent(
          new CustomEvent('validity', {
            detail: {
              name: 'ssn',
              isValid: ssnField.validity.valid && ssnField.value !== '',
            },
          })
        );
      }
      this.requestUpdate();
    }, 0);
  }

  async editClicked() {
    this.touchedField = true;

    const ssnField = this.shadowRoot.getElementById(this.ssnId);
    ssnField.setAttribute('value', '');
    this.values.ssn = '';
    this.requestUpdate();
    await this.updateComplete;
    ssnField.focus();
  }

  renderReadOnly() {
    return html` <fs-text-field
      label="Social security number"
      placeholder="•••-••-${this.lastFourTaxId}"
      readonly
      id="${this.coAppPrefix}read-only-ssn"
      required
    >
      Social Security number
      <fs-tooltip
        slot="trailing-icon"
        position="top"
        tooltip-content="Edit social security number"
      >
        <fs-icon
          description="Edit social security number"
          icon="fs-24-edit"
          interactive
          @click="${this.editClicked}"
        >
        </fs-icon>
      </fs-tooltip>
    </fs-text-field>`;
  }

  renderIconicV2() {
    // Show editable field if we do not need to mask
    this.showField = !this.checkMaskPrefill();

    return html`
      <div class="${this.showField ? 'hidden' : ''} read-only">
        ${this.renderReadOnly()}
      </div>
      <div class="${this.showField ? '' : 'hidden'} editable-field">
        ${this.renderSsnField()}
      </div>
    `;
  }

  renderSsnField() {
    this.debug.log('required', this.checkRequired());
    return html`
      <div>
        <div class="personal__ssn">
          <refi-loading-spinner-small
            ?hidden="${!this.pendingValidation}"
            message="Validating..."
          ></refi-loading-spinner-small>
          <fs-ssn-field
            class="includes-error-message"
            id="${this.ssnId}"
            name="${this.fieldName}"
            type="text"
            value-missing-message="Please enter a valid Social Security number XXX-XX-XXXX"
            maxLength="11"
            ?dangerously-disable-pattern-validation="${this.iconicV2 ||
            REFI_PERSONAL_CONST.isDevEnv() ||
            REFI_PERSONAL_CONST.isQAEnv() ||
            REFI_PERSONAL_CONST.isSBEnv() ||
            REFI_PERSONAL_CONST.isLocalEnv()}"
            ?required="${!this.iconicV2 ||
            (this.iconicV2 && this.checkRequired())}"
            value="${this.values.ssn}"
            pattern-mismatch-message="Please enter a valid Social Security number XXX-XX-XXXX"
            @change="${(ev) => {
              this.changed('ssn', ev);
            }}"
            @blur="${this.blurred}"
            autofocus
            aria-describedby="${`${this.ssnId}-ssnMismatch`}"
          >
            Social Security number
          </fs-ssn-field>
          <fs-body-text
            size="small"
            variant="feedback-negative"
            ?hidden="${!this.ssnMatch}"
            id="${`${this.ssnId}-ssnMismatch`}"
          >
            Co-applicant’s SSN cannot be the same as Primary Applicant’s SSN
          </fs-body-text>
        </div>
      </div>
    `;
  }

  render() {
    if (this.iconicV2) {
      return this.renderIconicV2();
    }
    return this.renderSsnField();
  }

  checkRequired() {
    // SSN is not required for iconic V2 if lastFourTaxId is prefilled and user has not changed the ssn
    if (
      this.iconicV2 &&
      this.isPrimary &&
      this.application.profileType === REFI_PERSONAL_CONST.FINANCIAL &&
      !this.touchedField &&
      this.lastFourTaxId &&
      !this.values.ssn
    ) {
      if (!ApplicationService.getPrimaryApplicantSsnChanged) {
        return false;
      }
    }
    return true;
  }

  checkMaskPrefill() {
    // SSN needs to be shown in readonly field if the lastFourTaxId has been prefilled and the user has not changed it
    if (
      this.iconicV2 &&
      this.isPrimary &&
      this.application.profileType === REFI_PERSONAL_CONST.FINANCIAL &&
      !this.touchedField &&
      this.lastFourTaxId &&
      !this.values.ssn
    ) {
      if (!ApplicationService.getPrimaryApplicantSsnChanged) {
        return true;
      }
    }
    return false;
  }

  updateSsnChangedTag() {
    // SSN has changed only if the user has touched it
    if (this.isPrimary && this.touchedField) {
      ApplicationService.primaryApplicantSsnChanged = true;
    } else if (!this.isPrimary && this.touchedField) {
      ApplicationService.coApplicantSsnChanged = true;
    }
  }

  firstUpdated() {
    const ssnField = this.shadowRoot.getElementById(this.ssnId);
    ssnField.addEventListener('paste', (event) => event.preventDefault());
    this.dispatchEvent(
      new CustomEvent('inputFields', {
        detail: { name: 'ssn', value: ssnField },
      })
    );
    this.showField = !this.checkMaskPrefill();
  }

  blurred(ev) {
    this.changed('ssn', ev);
    this.compareSsns();
    if (ev.target.validity.valid) {
      stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
        replacements: { value: this.ssnId },
      });
    } else {
      stratum.publishTag(
        TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
        {
          replacements: {
            value: 'Please enter a valid Social Security number XXX-XX-XXXX',
          },
        }
      );
    }
    this.requestUpdate();
  }

  changed(name, ev) {
    const { value } = ev.target;
    const ssnVal = value.replace(/-/g, '');

    this.validatedAgainstPrimaryapp = false;
    if (ev.target.validity.valid) {
      this.values.ssn = ev?.target?.maskedValue.trim();
    }
    if (ssnVal !== this.entered && ssnVal.length === 9) {
      this.compareSsns();
    }
    this.entered = ssnVal;

    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: { name, isValid: ev.target.validity.valid },
      })
    );
  }

  runBusinessChecks() {
    if (!this.validatedAgainstPrimaryapp) {
      return this.compareSsns();
    }
    if (this.checkMaskPrefill() && !this.checkRequired()) {
      // lastFourTaxId is prefilled and hasn't changed, so we don't need to validate
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'ssn',
            isValid: true,
          },
        })
      );
      this.debug.log('Prefilled and not required, valid');
    } else if (this.checkMaskPrefill() && this.checkRequired()) {
      const ssnField = this.shadowRoot.getElementById(this.ssnId);
      ssnField.setAttribute('value', this.values.ssn);
      ssnField.checkValidity();

      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'ssn',
            isValid: ssnField.validity.valid && ssnField.value !== '',
          },
        })
      );
      this.debug.log(
        'Prefilled and required, validity ',
        ssnField.validity.valid
      );
    }
    return Promise.resolve();
  }

  setSsnMatch(ssnMatch) {
    if (ssnMatch) {
      this.ssnMatch = true;
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'compareSsns',
            isValid: false,
          },
        })
      );
    } else {
      this.ssnMatch = false;
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'compareSsns',
            isValid: true,
          },
        })
      );
    }
    this.debug.log('Ssnmatch updated to', this.ssnMatch);
    this.requestUpdate();
  }

  compareManualSsns() {
    if (this.values.ssn && this.values.ssn.length > 0) {
      if (
        this.isPrimary &&
        this.application.coApplicant.ssn &&
        !this.isCommunicationStepV2
      ) {
        this.ssnMatch = this.values.ssn === this.application.coApplicant.ssn;
      } else if (
        (this.isPrimary && this.iconicV2) ||
        !this.application.primaryApplicant.ssn
      ) {
        this.dispatchEvent(
          new CustomEvent('ssn-updated', {
            detail: { ssn: this.values.ssn, isPrimary: this.isPrimary },
            bubbles: true,
            composed: true,
          })
        );
      } else if (!this.isPrimary) {
        this.ssnMatch =
          this.values.ssn === this.application.primaryApplicant.ssn;
      }
    }
  }

  async compareSsns() {
    // Make sure primary and coapplicant SSNs are not the same
    if (this.application.applicationType === 'JOINT' || this.iconicV2) {
      this.compareManualSsns();

      // If on coapplicant ssn and primary ssn is prefilled, validate against primary
      if (
        (this.iconicV2 &&
          !this.ssnMatch &&
          !this.application.primaryApplicant.ssn &&
          !this.isPrimary &&
          this.application.primaryApplicant.lastFourTaxId) ||
        (!this.iconicV2 &&
          !this.ssnMatch &&
          !this.application.primaryApplicant.ssn &&
          !this.isPrimary)
      ) {
        this.dispatchEvent(new CustomEvent('pending', { detail: true }));
        this.pendingValidation = true;
        this.ssnMatch = !(await this.validateCoappSsn(this.values.ssn));
        this.validatedAgainstPrimaryapp = true;
        this.pendingValidation = false;
        this.dispatchEvent(new CustomEvent('pending', { detail: false }));

        REFI_PERSONAL_CONST.TRACK_EVENT(
          `coapp ssn validation ${(!this.ssnMatch).toString()}`
        );
        stratum.publishTag(
          TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
          {
            replacements: {
              value: this.ssnMatch
                ? 'Coapplicant SSN cannot be the same as the stored primary SSN'
                : 'Coapplicant SSN was successfully checked against the primary SSN stored',
            },
          }
        );
      }

      if (this.ssnMatch && !!this.application.primaryApplicant.ssn) {
        stratum.publishTag(
          TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
          {
            replacements: {
              value:
                "Your co-applicant's Social Security Number cannot be the same as the primary applicant's Social Security Number",
            },
          }
        );
      }

      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'compareSsns',
            isValid: !this.ssnMatch,
          },
        })
      );
    }
    this.requestUpdate();
  }

  async validateCoappSsn(ssn) {
    if (!HighlineService.publicKey) {
      if (!this.publicKeyPromise) {
        HighlineService.urlPrefix = REFI_PERSONAL_CONST.HIGHLINE_URL_PREFIX();
        this.publicKeyPromise = HighlineService.getPublicKey(
          REFI_PERSONAL_CONST.PREQUAL_BFF_HIGHLINE_PRODUCT_ID()
        )
          .then((key) => {
            HighlineService.publicKey = key;
            this.publicKeyPromise = null; // Reset the promise after it resolves
          })
          .catch((error) => {
            this.publicKeyPromise = null; // Reset the promise if there's an error
            throw error;
          });
      }
      await this.publicKeyPromise;
    }

    if (!this.encryptionPromise) {
      this.encryptionPromise = HighlineService.encryptWithPublicKey(ssn)
        .then((resp) => ({
          applicantDetail: [
            { applicantType: 'COAPPLICANT', encryptedSsn: resp },
          ],
        }))
        .then((body) =>
          ApiService.post(
            REFI_PERSONAL_CONST.VALIDATE_CUSTOMER_DETAILS_URL,
            body
          )
        )
        .then((resp) => {
          this.encryptionPromise = null; // Reset the promise after it resolves
          return !resp.errors[0];
        })
        .catch((error) => {
          this.encryptionPromise = null; // Reset the promise if there's an error
          throw error;
        });
    }
    return this.encryptionPromise;
    /*
    return HighlineService.encryptWithPublicKey(ssn)
      .then((resp) => ({
        applicantDetail: [{ applicantType: 'COAPPLICANT', encryptedSsn: resp }],
      }))
      .then((body) =>
        ApiService.post(REFI_PERSONAL_CONST.VALIDATE_CUSTOMER_DETAILS_URL, body)
      )
      .then((resp) => !resp.errors[0]);
      */
  }
}

customElements.define('refi-personal-ssn', RefiPersonalSSNComponent);
